// Submenú "Reportes" del módulo Control Bancario (solo lectura + export CSV).
//   Libro bancos · Estado de conciliación · Reporte de cheques · Flujo de efectivo
import { useCallback, useMemo, useState } from 'react';
import { AvisoRenombre } from '@/components/ui/AvisoRenombre';
import { useQuery } from '@tanstack/react-query';
import { Activity, BookOpen, ClipboardCheck, Download, FileText, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  CATEGORIA_MOV, Campo, CuentaMini, ESTADO_CHEQUE, ESTADO_CHEQUE_CLS, LibroBanco, Movimiento, RepConciliacion, RepFlujo,
  SortTh, cb, cbRpc, exportarCSV, fieldCls, hoyISO, label, money, useSort,
} from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

function useCuentas() {
  return useQuery<CuentaMini[]>({
    queryKey: ['cb-cuentas-mini'],
    queryFn: () => cb.list<CuentaMini>('cuentas', '?select=id,alias,numero,moneda,banco:banco_id(nombre)&deleted_at=is.null&order=alias,numero'),
  });
}
const primerDiaMes = () => hoyISO().slice(0, 8) + '01';

// ============================================================ LIBRO BANCOS =====
export function LibroBancos() {
  const { data: cuentas = [] } = useCuentas();
  const [cuentaId, setCuentaId] = useState('');
  const cta = cuentaId || cuentas[0]?.id || '';
  const [desde, setDesde] = useState(primerDiaMes());
  const [hasta, setHasta] = useState(hoyISO());
  const [fCategoria, setFCategoria] = useState('');
  const [fReferencia, setFReferencia] = useState('');
  const [fConcepto, setFConcepto] = useState('');
  const { data, isFetching } = useQuery<LibroBanco>({
    queryKey: ['cb-libro', cta, desde, hasta],
    queryFn: () => cbRpc<LibroBanco>('cb_movimientos', { _cuenta_id: cta, _desde: desde || null, _hasta: hasta || null }),
    enabled: !!cta,
  });
  const movs = useMemo(() => data?.movimientos ?? [], [data]);
  const moneda = data?.cuenta.moneda ?? 'USD';
  const filtrando = !!(fCategoria || fReferencia.trim() || fConcepto.trim());
  const movsFiltrados = useMemo(() => movs.filter((m) =>
    (!fCategoria || (m.categoria ?? '') === fCategoria) &&
    (!fReferencia.trim() || (m.referencia ?? '').toLowerCase().includes(fReferencia.trim().toLowerCase())) &&
    (!fConcepto.trim() || (m.concepto ?? '').toLowerCase().includes(fConcepto.trim().toLowerCase()))
  ), [movs, fCategoria, fReferencia, fConcepto]);
  const getVal = useCallback((m: Movimiento, key: string): string | number | null => {
    switch (key) {
      case 'fecha': return m.fecha;
      case 'concepto': return m.concepto ?? '';
      case 'referencia': return m.referencia ?? '';
      case 'debito': return m.tipo === 'debito' ? Number(m.monto) : null;
      case 'credito': return m.tipo === 'credito' ? Number(m.monto) : null;
      case 'saldo': return Number(m.saldo);
      default: return null;
    }
  }, []);
  const sort = useSort(movsFiltrados, getVal);

  // Exportar (CSV / XLS / PDF) — respeta filtros y orden actuales
  const nombreArchivo = `libro-bancos-${fechaLocal()}`;
  const headersExp = ['N°', 'Fecha', 'Concepto', 'Referencia', 'Débito', 'Crédito', 'Saldo'];
  const totDebito = sort.sorted.filter((m) => m.tipo === 'debito').reduce((a, m) => a + Number(m.monto), 0);
  const totCredito = sort.sorted.filter((m) => m.tipo === 'credito').reduce((a, m) => a + Number(m.monto), 0);
  const filasExport: Celda[][] = [
    ...sort.sorted.map((m, i): Celda[] => [
      i + 1, m.fecha, m.concepto ?? '', m.referencia ?? '',
      m.tipo === 'debito' ? Number(m.monto) : '', m.tipo === 'credito' ? Number(m.monto) : '', Number(m.saldo),
    ]),
    ['', '', '', 'TOTAL', totDebito, totCredito, Number(data?.saldo_final ?? 0)],
  ];

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><BookOpen className="w-5 h-5" /> Libro bancos (por cuenta y período)</h1>
      <Card><CardContent className="py-3 flex flex-wrap items-end gap-3">
        <Campo label="Cuenta" className="min-w-[220px]">
          <select className={fieldCls} value={cta} onChange={(e) => setCuentaId(e.target.value)}>
            {cuentas.length === 0 && <option value="">Sin cuentas</option>}
            {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}
          </select>
        </Campo>
        <Campo label="Desde"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
        <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        <Campo label="Categoría" className="min-w-[160px]">
          <select className={fieldCls} value={fCategoria} onChange={(e) => setFCategoria(e.target.value)}>
            <option value="">Todas</option>
            {CATEGORIA_MOV.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </Campo>
        <Campo label="Referencia"><input className={fieldCls} value={fReferencia} onChange={(e) => setFReferencia(e.target.value)} placeholder="Nº doc / comprobante" /></Campo>
        <Campo label="Concepto" className="min-w-[200px]"><input className={fieldCls} value={fConcepto} onChange={(e) => setFConcepto(e.target.value)} placeholder="Texto del concepto" /></Campo>
        {filtrando && <Button variant="outline" onClick={() => { setFCategoria(''); setFReferencia(''); setFConcepto(''); }}>Limpiar</Button>}
        <ExportarBotones
          nombre={nombreArchivo} titulo="Libro bancos" headers={headersExp} filas={filasExport}
          colsDerecha={[0, 4, 5, 6]} subtitulo={`${new Date().toLocaleString(LOCALE)} · ${sort.sorted.length} movimiento(s)`}
          fmtPDF={(v, c) => ([4, 5, 6].includes(c) ? (v === '' ? '' : money(Number(v), moneda)) : String(v ?? ''))}
          disabled={sort.sorted.length === 0} />
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Saldo inicial" valor={money(data?.saldo_inicial_periodo, moneda)} />
        <Kpi label="Créditos" valor={money(data?.totales.creditos, moneda)} clase="text-estable" />
        <Kpi label="Débitos" valor={money(data?.totales.debitos, moneda)} clase="text-pendiente" />
        <Kpi label="Saldo final" valor={money(data?.saldo_final, moneda)} fuerte />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Movimientos ({filtrando ? `${movsFiltrados.length} de ${movs.length}` : movs.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right w-12">N°</th><SortTh col="fecha" sort={sort}>Fecha</SortTh><SortTh col="concepto" sort={sort}>Concepto</SortTh><SortTh col="referencia" sort={sort}>Ref.</SortTh><SortTh col="debito" sort={sort} align="right">Débito</SortTh><SortTh col="credito" sort={sort} align="right">Crédito</SortTh><SortTh col="saldo" sort={sort} align="right">Saldo</SortTh>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">{filtrando ? 'Sin movimientos que coincidan con el filtro' : 'Sin movimientos'}</td></tr>}
              {sort.sorted.map((m, i) => (
                <tr key={m.id} className={`border-b last:border-0 ${m.estado === 'anulado' ? 'opacity-50 line-through' : ''}`}>
                  <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{m.fecha}</td>
                  <td className="py-2 pr-3">{m.concepto ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{m.referencia ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-pendiente">{m.tipo === 'debito' ? money(m.monto, moneda) : ''}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-estable">{m.tipo === 'credito' ? money(m.monto, moneda) : ''}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(m.saldo, moneda)}</td>
                </tr>
              ))}
              {sort.sorted.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3 text-right" colSpan={4}>Totales ({sort.sorted.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-pendiente">{money(sort.sorted.filter((m) => m.tipo === 'debito').reduce((a, m) => a + Number(m.monto), 0), moneda)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-estable">{money(sort.sorted.filter((m) => m.tipo === 'credito').reduce((a, m) => a + Number(m.monto), 0), moneda)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(data?.saldo_final, moneda)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================================================== ESTADO DE CONCILIACIÓN ===
export function ReporteConciliacion() {
  const { data: rows = [], isFetching } = useQuery<RepConciliacion[]>({
    queryKey: ['cb-rep-conciliacion'],
    queryFn: () => cbRpc<RepConciliacion[]>('cb_rep_conciliacion'),
  });
  const exportar = () => exportarCSV('estado-conciliacion',
    ['Banco', 'Cuenta', 'Moneda', 'Saldo libro', 'Saldo extracto', 'Diferencia', 'Mov. pend.', 'Líneas pend.', 'Última conciliación'],
    rows.map((r) => [r.banco, r.cuenta, r.moneda, r.saldo_libro, r.saldo_extracto, r.saldo_libro - r.saldo_extracto, r.mov_pendientes, r.lin_pendientes, r.ultima_conciliacion]));

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><ClipboardCheck className="w-5 h-5" /> Reporte de conciliación bancaria</h1>
        <Button variant="outline" onClick={exportar} disabled={rows.length === 0}><Download className="w-4 h-4 mr-1" /> CSV</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Cuentas ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Banco</th><th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3 text-right">Saldo libro</th><th className="py-2 pr-3 text-right">Saldo extracto</th><th className="py-2 pr-3 text-right">Diferencia</th><th className="py-2 pr-3 text-right">Mov. pend.</th><th className="py-2 pr-3 text-right">Líneas pend.</th><th className="py-2 pr-3">Última concil.</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin cuentas</td></tr>}
              {rows.map((r) => {
                const dif = r.saldo_libro - r.saldo_extracto;
                return (
                  <tr key={r.cuenta_id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3">{r.banco}</td>
                    <td className="py-2 pr-3">{r.cuenta}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.saldo_libro, r.moneda)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.saldo_extracto, r.moneda)}</td>
                    <td className={`py-2 pr-3 text-right font-mono tabular-nums ${Math.abs(dif) > 0.005 ? 'text-pendiente' : 'text-estable'}`}>{money(dif, r.moneda)}</td>
                    <td className="py-2 pr-3 text-right font-mono">{r.mov_pendientes}</td>
                    <td className="py-2 pr-3 text-right font-mono">{r.lin_pendientes}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{r.ultima_conciliacion ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================================================== REPORTE DE CHEQUES =======
interface RepCheques { cheques: { id: string; numero: number; fecha_emision: string; fecha_cobro: string; beneficiario: string; concepto: string | null; monto: number; estado: string; cuenta: string; moneda: string }[]; resumen: { estado: string; n: number; monto: number }[]; }
export function ReporteCheques() {
  const { data: cuentas = [] } = useCuentas();
  const [desde, setDesde] = useState(primerDiaMes());
  const [hasta, setHasta] = useState(hoyISO());
  const [cuentaId, setCuentaId] = useState('');
  const [estado, setEstado] = useState('');
  const { data, isFetching } = useQuery<RepCheques>({
    queryKey: ['cb-rep-cheques', desde, hasta, cuentaId, estado],
    queryFn: () => cbRpc<RepCheques>('cb_rep_cheques', { _desde: desde || null, _hasta: hasta || null, _cuenta_id: cuentaId || null, _estado: estado || null }),
  });
  const cheques = data?.cheques ?? [];
  const headersExp = ['N°', 'Número', 'Emisión', 'Cobro', 'Beneficiario', 'Cuenta', 'Monto', 'Estado'];
  const totMonto = cheques.reduce((a, c) => a + Number(c.monto), 0);
  const filasExport: Celda[][] = [
    ...cheques.map((c, i): Celda[] => [
      i + 1, c.numero, c.fecha_emision, c.fecha_cobro, c.beneficiario, c.cuenta, Number(c.monto), label(ESTADO_CHEQUE, c.estado),
    ]),
    ['', '', '', '', '', 'TOTAL', totMonto, ''],
  ];

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><FileText className="w-5 h-5" /> Reporte de cheques</h1>
      <Card><CardContent className="py-3 flex flex-wrap items-end gap-3">
        <Campo label="Desde"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
        <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        <Campo label="Cuenta"><select className={fieldCls} value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}><option value="">Todas</option>{cuentas.map((c) => <option key={c.id} value={c.id}>{c.alias ?? c.numero}</option>)}</select></Campo>
        <Campo label="Estado"><select className={fieldCls} value={estado} onChange={(e) => setEstado(e.target.value)}><option value="">Todos</option>{ESTADO_CHEQUE.map((e) => <option key={e.v} value={e.v}>{e.l}</option>)}</select></Campo>
        <ExportarBotones
          nombre={`cheques-${fechaLocal()}`} titulo="Reporte de cheques" headers={headersExp} filas={filasExport}
          colsDerecha={[0, 6]} subtitulo={`${new Date().toLocaleString(LOCALE)} · ${cheques.length} cheque(s)`}
          fmtPDF={(v, c) => (c === 6 ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
          disabled={cheques.length === 0} />
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(data?.resumen ?? []).map((r) => (
          <Card key={r.estado}><CardContent className="py-3">
            <div className="text-xs text-muted-foreground capitalize">{label(ESTADO_CHEQUE, r.estado)} · {r.n}</div>
            <div className="text-base font-mono tabular-nums font-semibold">{money(r.monto)}</div>
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Cheques ({cheques.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Emisión</th><th className="py-2 pr-3">Cobro</th><th className="py-2 pr-3">Beneficiario</th><th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Estado</th>
            </tr></thead>
            <tbody>
              {cheques.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin cheques</td></tr>}
              {cheques.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{c.numero}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{c.fecha_emision}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{c.fecha_cobro}</td>
                  <td className="py-2 pr-3">{c.beneficiario}</td>
                  <td className="py-2 pr-3">{c.cuenta}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.monto, c.moneda)}</td>
                  <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO_CHEQUE_CLS[c.estado] ?? 'bg-muted'}`}>{label(ESTADO_CHEQUE, c.estado)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================================================== FLUJO DE EFECTIVO ========
export function FlujoEfectivo() {
  const { data: cuentas = [] } = useCuentas();
  const [desde, setDesde] = useState(primerDiaMes());
  const [hasta, setHasta] = useState(hoyISO());
  const [cuentaId, setCuentaId] = useState('');
  const { data, isFetching } = useQuery<RepFlujo>({
    queryKey: ['cb-rep-flujo', desde, hasta, cuentaId],
    queryFn: () => cbRpc<RepFlujo>('cb_rep_flujo_efectivo', { _desde: desde, _hasta: hasta, _cuenta_id: cuentaId || null }),
  });

  return (
    <div className="p-6 space-y-4">
      <AvisoRenombre clave="cb-rep-flujo" antes="Flujo de efectivo" />
      <h1 className="text-xl font-semibold flex items-center gap-2"><Activity className="w-5 h-5" /> Flujo de efectivo por categoría</h1>
      <p className="text-sm text-muted-foreground">Estado de flujo de efectivo realizado en el período (movimientos por categoría).</p>
      <Card><CardContent className="py-3 flex flex-wrap items-end gap-3">
        <Campo label="Desde"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
        <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        <Campo label="Cuenta"><select className={fieldCls} value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}><option value="">Todas</option>{cuentas.map((c) => <option key={c.id} value={c.id}>{c.alias ?? c.numero}</option>)}</select></Campo>
        {isFetching && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Saldo inicial" valor={money(data?.saldo_inicial)} />
        <Kpi label="Total ingresos" valor={money(data?.total_ingresos)} clase="text-estable" />
        <Kpi label="Total egresos" valor={money(data?.total_egresos)} clase="text-pendiente" />
        <Kpi label="Saldo final" valor={money(data?.saldo_final)} fuerte />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base text-estable">Ingresos por categoría</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <TablaCat rows={data?.ingresos ?? []} total={data?.total_ingresos ?? 0} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base text-pendiente">Egresos por categoría</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <TablaCat rows={data?.egresos ?? []} total={data?.total_egresos ?? 0} />
          </CardContent>
        </Card>
      </div>

      <Card><CardContent className="py-3">
        <div className="flex justify-between text-sm"><span className="text-muted-foreground">Saldo inicial</span><span className="font-mono">{money(data?.saldo_inicial)}</span></div>
        <div className="flex justify-between text-sm"><span className="text-muted-foreground">(+) Ingresos</span><span className="font-mono text-estable">{money(data?.total_ingresos)}</span></div>
        <div className="flex justify-between text-sm"><span className="text-muted-foreground">(−) Egresos</span><span className="font-mono text-pendiente">{money(data?.total_egresos)}</span></div>
        <div className="flex justify-between border-t pt-1 mt-1 font-semibold"><span>Saldo final</span><span className="font-mono">{money(data?.saldo_final)}</span></div>
      </CardContent></Card>
    </div>
  );
}
function TablaCat({ rows, total }: { rows: { categoria: string; total: number }[]; total: number }) {
  return (
    <table className="w-full text-sm">
      <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Categoría</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3 text-right">%</th></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">Sin movimientos</td></tr>}
        {rows.map((r) => (
          <tr key={r.categoria} className="border-b last:border-0">
            <td className="py-2 pr-3">{label(CATEGORIA_MOV, r.categoria)}</td>
            <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.total)}</td>
            <td className="py-2 pr-3 text-right text-muted-foreground">{total > 0 ? Math.round((r.total / total) * 100) : 0}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Kpi({ label: l, valor, clase, fuerte }: { label: string; valor: string; clase?: string; fuerte?: boolean }) {
  return (
    <Card><CardContent className="py-3">
      <div className="text-xs text-muted-foreground">{l}</div>
      <div className={`font-mono tabular-nums ${fuerte ? 'text-lg font-semibold' : 'text-base'} ${clase ?? ''}`}>{valor}</div>
    </CardContent></Card>
  );
}
